import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { useRoles } from "../lib/useRoles";
import { invalidateClientPerms } from "../lib/useClientPerms";

// Master-only: create / delete custom roles (client or staff). Permissions for a new role start as a copy of its base role.
export function RoleManager({ kind, onChanged }) {
  const roles = useRoles();
  const list = roles[kind];
  const bases = list.filter((r) => r.builtin && r.key !== "master");
  const [label, setLabel] = useState("");
  const [base, setBase] = useState(bases[0]?.key || "");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);   // {key, label, holders}
  const [reassign, setReassign] = useState("");

  const refresh = () => { roles.reload(); invalidateClientPerms(); onChanged?.(); };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/settings/roles", { kind, label, base: base || bases[0]?.key });
      toast.success(`Role "${label}" created — adjust its View/Edit boxes below and save`);
      setLabel(""); refresh();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (r, to = "") => {
    setBusy(true);
    try {
      await api.delete(`/settings/roles/${kind}/${r.key}`, { params: to ? { reassign_to: to } : {} });
      toast.success(`Role "${r.label}" deleted`);
      setPending(null); refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || apiErr(e);
      if (e?.response?.status === 409) { setPending({ ...r, msg }); setReassign(""); } else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`role-manager-${kind}`} className="border border-dashed border-border bg-neutral-50/60 p-4 mb-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground self-center">Custom {kind} roles</div>
        {list.filter((r) => !r.builtin).map((r) => (
          <span key={r.key} data-testid={`role-chip-${r.key}`} className="inline-flex items-center gap-2 border border-black bg-white px-2.5 py-1 text-xs">
            {r.label} <span className="text-muted-foreground">· based on {roles.label(kind, r.base)}</span>
            <button data-testid={`role-delete-${r.key}`} onClick={() => remove(r)} disabled={busy} title="Delete role" className="text-muted-foreground hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
          </span>
        ))}
        {list.filter((r) => !r.builtin).length === 0 && <span className="text-xs text-muted-foreground self-center">None yet — the built-in roles cover everyone.</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <input data-testid={`role-new-label-${kind}`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="New role name (e.g. Bookkeeper)" maxLength={40}
          className="border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black w-64" />
        <select data-testid={`role-new-base-${kind}`} value={base || bases[0]?.key || ""} onChange={(e) => setBase(e.target.value)} className="border border-input bg-white px-3 py-2 text-sm">
          {bases.map((b) => <option key={b.key} value={b.key}>starts as {b.label}</option>)}
        </select>
        <button data-testid={`role-create-${kind}`} onClick={create} disabled={busy || label.trim().length < 2}
          className="inline-flex items-center gap-1.5 bg-black text-white px-4 py-2 text-sm font-medium hover:bg-neutral-800 disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Add role</button>
      </div>
      {pending && (
        <div data-testid="role-delete-reassign" className="mt-3 border border-amber-400 bg-amber-50 p-3 text-xs flex flex-wrap items-center gap-2">
          <span className="text-amber-900">{pending.msg}</span>
          <select data-testid="role-reassign-select" value={reassign} onChange={(e) => setReassign(e.target.value)} className="border border-input bg-white px-2 py-1 text-xs">
            <option value="">Move them to…</option>
            {list.filter((r) => r.key !== pending.key && r.key !== "master").map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <button data-testid="role-reassign-confirm" disabled={!reassign || busy} onClick={() => remove(pending, reassign)} className="bg-red-600 text-white px-3 py-1 font-medium disabled:opacity-50">Move & delete</button>
          <button onClick={() => setPending(null)} className="underline">Cancel</button>
        </div>
      )}
    </div>
  );
}
